<script setup>
import {shallowRef, computed, watch} from 'vue';
import {useStore} from '@/core/plugins/store';
import {useI18n} from 'vue-i18n';
import * as echarts from 'echarts/core';
import {LineChart} from 'echarts/charts';
import {UniversalTransition} from 'echarts/features';
import {CanvasRenderer} from 'echarts/renderers';
import {GridComponent, TooltipComponent, LegendComponent, ToolboxComponent, DataZoomComponent} from 'echarts/components';

import {useObserveResize} from '@/core/composables/useObservebleResize';
import {useChartTheme} from '@/core/composables/useChartTheme';
import ChartFilters from '@/components/Filters/ChartFilters.vue';
import ThemeChanger from '@/components/ThemeChanger.vue';
import {CHART_TYPE} from '@/core/constants/query';

echarts.use([
    ToolboxComponent,
    DataZoomComponent,
    TooltipComponent,
    GridComponent,
    LegendComponent,
    CanvasRenderer,
    LineChart,
    UniversalTransition,
]);

const {t} = useI18n();
const {store} = useStore();
const {themeTitles, currentTheme, selectedThemeTitle, changeTheme} = useChartTheme();

const target = shallowRef(null);
const chartInstance = shallowRef(null);

const statuses = computed(() => {
    if (!store.value.chartData?.chart.datasets.length) return null;

    return store.value.chartData?.chart.datasets.map((obj) => obj.name).toReversed();
});

const heightCoefficient = (statuses.value?.length || 0) * 4;

watch(store, setOptions);
watch(target, (newValue) => {
    newValue && init();
});

function init() {
    if (chartInstance.value) {
        chartInstance.value.dispose();
    }

    chartInstance.value = echarts.init(target.value, currentTheme.value, {
        renderer: 'canvas',
        useDirtyRect: false,
        height: 500 + heightCoefficient,
    });

    setOptions();
}

function setOptions() {
    const option = getOptions();

    chartInstance.value?.setOption(option);
}

function getOptions() {
    const {chart} = store.value.chartData;

    return {
        xAxis: [
            {
                type: 'category',
                boundaryGap: false,
                data: chart.labels,
            },
        ],
        yAxis: [
            {
                name: 'Карточки',
                type: 'value',
            },
        ],
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
            },
            formatter(params) {
                const filteredByValue = params.filter(({value}) => value > 0);

                if (filteredByValue.length <= 0) {
                    return '';
                }

                let template = '';

                if (filteredByValue?.[0]?.axisValue) {
                    template += `${filteredByValue[0].axisValue} <br>`;
                }

                filteredByValue.forEach((param, index) => {
                    template += `<div style="display: flex;">
                        <div>${param.marker} ${param.seriesName}</div>
                        <div style="margin-left: auto; padding-left: 10px;"><strong>${param.value}</strong></div>
                        ${index !== filteredByValue.length - 1 ? '<br>' : ''}
                    </div>`;
                });

                return template;
            },
        },
        legend: {
            data: statuses.value,
            left: 10,
            right: 20,
            width: '95%',
        },
        toolbox: {
            top: 90,
            right: 10,
            feature: {
                saveAsImage: {
                    title: 'Save',
                },
            },
        },
        dataZoom: [
            {
                type: 'inside',
            },
            {
                type: 'slider',
            },
        ],
        grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            top: 130 + heightCoefficient,
            containLabel: true,
        },
        series: [
            ...chart.datasets.toReversed().map(({name, data}) => {
                return {
                    name,
                    type: 'line',
                    stack: 'Total',
                    areaStyle: {},
                    emphasis: {
                        focus: 'series',
                    },
                    data,
                };
            }),
        ],
    };
}

useObserveResize(chartInstance, target);
</script>

<template>
    <div class="cfd-chart">
        <ChartFilters :chart-type="CHART_TYPE.CFD" />
        <div class="cfd-chart-buttons">
            <ThemeChanger
                :theme-titles="themeTitles"
                :themes="store.loadedThemes"
                :selected-theme-title="selectedThemeTitle"
                @change-theme="changeTheme($event, init)"
            />
        </div>
        <div
            v-if="statuses"
            id="cfd-chart-target"
            ref="target"
            class="cfd-chart-target"
        />
        <h3
            v-else
            class="cfd-chart-info"
        >
            {{ t('label_cfd_nodata') }}
        </h3>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/vars.sass';

.cfd-chart {
    &-buttons {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        margin: 24px 0 16px;
    }

    &-target {
        width: 100%;
        height: 100%;
        background-color: $white;
        margin-bottom: 10px;
    }

    &-info {
        margin-top: 50px;
        text-align: center;
    }
}
</style>
